import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { ANYWHERE_SEARCH_ITEM, searchVerticalItemsByKey } from 'constants/search'
import { useGlobalExperiencesSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalExperiencesSearch'
import { GLOBAL_SEARCH_INITIAL_STATE } from 'contexts/GlobalSearch/GlobalSearchState'
import ExperiencesTabContent from '../Components/Mobile/TabContents/ExperiencesTabContent'
import { pushWithRegion } from 'actions/NavigationActions'
import { replaceExperienceQueryString } from 'lib/experiences/experienceUtils'
import useQueryParams from 'hooks/useQueryParams'
import LineHotAirBalloonIcon from 'LuxKit/Icons/line/LineHotAirBalloonIcon'
import { useAppDispatch } from 'hooks/reduxHooks'
import * as Analytics from 'analytics/analytics'
import { searchEventWithContext } from 'analytics/snowplow/events'
import { mapGlobalSearchContextToSnowplowSearchEvent } from 'analytics/mapSnowplowSearchTracking'

function useGlobalSearchVerticalTabExperiences(): App.GlobalSearchVerticalTabV2 {
  const dispatch = useAppDispatch()
  const queryParams = useQueryParams()

  const {
    globalExperiencesSearchDispatch: searchDispatch,
    globalExperiencesSearchState: state,
  } = useGlobalExperiencesSearchContext(GLOBAL_SEARCH_INITIAL_STATE, queryParams)

  const onSearch = useCallback(() => {
    const {
      experienceCategories,
      searchItem,
    } = state

    Analytics.trackEvent(searchEventWithContext({
      searchEvent: { type: 'search_bar' },
      sortContext: { value: 'recommended' },
      searchContext: mapGlobalSearchContextToSnowplowSearchEvent({
        searchItem,
        searchVerticals:
        new Set(['experiences']),
      },
      ),
    }))

    const toBeSearchedItem = searchItem ?? ANYWHERE_SEARCH_ITEM
    const query = replaceExperienceQueryString({
      destinationId: toBeSearchedItem.value,
      destinationName: toBeSearchedItem.format.mainText,
      categories: experienceCategories,
    })
    dispatch(pushWithRegion(`/search/experiences?${query}`))
  }, [state, dispatch])

  const intl = useIntl()

  return useMemo((): App.GlobalSearchVerticalTabV2 => ({
    label: searchVerticalItemsByKey.get('experiences')?.label ?? intl.formatMessage({
      id: 'dyh3UtpbEgkrPMhF8tdt',
      defaultMessage: 'experiences',
      description: 'Fallback label for experiences search vertical tab',
    }),
    key: 'experiences',
    context: { dispatch: searchDispatch, state },
    onSearch,
    ContentComponent: ExperiencesTabContent,
    Icon: LineHotAirBalloonIcon,
  }), [searchDispatch, state, onSearch, intl])
}

export default useGlobalSearchVerticalTabExperiences
